import { Fragment, useState, useSyncExternalStore, type KeyboardEvent, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import type { BollettaVista, Condominio, Contatore, ContatoreGas, Esito, OffertaMenu } from "@/api/preventivo";
import { COLORE_TONO, altriInFila, annoBreve, conUnita, esitiConFrase, esitiGasVisibili, spreadScritto, suFornitture, tono } from "@/lib/preventivoFormato";
import { Pillola } from "./Esiti";
import {
  Altri, DETTAGLIO_NON_LEGGIBILE, EliminaBolletta, EtichettaTipo, FOCUS, Importo, Intestazione, MeseLink, NonLetta, Pcv, Ricarico, nomeConScadenza,
  Spread, avvisoGas, differenzaBolletta, eIncompleta, notaMotivo, offertaDel, offertaDelContatore, notaSpreadMancante,
} from "./parti";
import { SceltaOfferta } from "./SceltaOfferta";
import { useModo } from "./modo";
import {
  ADDEBITI_IN_PIU, COL_ALTRI, COL_MESE_FORNITURA, COL_DIFFERENZA, COL_PCV, COL_RICARICO, COL_SPREAD, FATTO, FUORI_CONFRONTO, NOTA_ALTRI,
  NOTA_OFFERTA, NOTA_RICARICO, NOTA_SPREAD, OFFERTA_CONDOMINIO, SCEGLI_OFFERTA, SOLO_QUESTA, IN_ANALISI,
  fornitureDiverse, AGGIUNGI_PAGINA, ELIMINA,
} from "./scritte";

/** Cosa è aperto nella tabella: il riquadro dell'offerta del condominio. */
export interface ApertoTabella {
  condominio: string;
}

interface Props {
  condomini: Condominio[];
  offerte: OffertaMenu[];
  aperto: ApertoTabella | null;
  /** Una scelta è in volo: menù spenti. */
  occupato: boolean;
  /** Il pulsante della riga di titolo: apre (o chiude) i menù dell'offerta del condominio. */
  onApriCondominio: (c: Condominio) => void;
  onChiudi: () => void;
  onScegli: (c: Condominio, offertaId: string) => void;
  /** I menù nella riga sottile di una fornitura; `null` = «Torna all'offerta del condominio». */
  onScegliContatore: (c: Condominio, k: Contatore, offertaId: string | null) => void;
  /** «Elimina» nella riga sottile di una bolletta: la pagina chiede conferma. */
  onElimina: (b: BollettaVista) => void;
  /** «Aggiungi pagina» nella riga di una bolletta incompleta fotografata; senza, il link non c'è. */
  onPagina?: (bollettaId: string) => void;
  /** Avviso di una scelta non salvata: nel riquadro del condominio, o sotto i menù della fornitura. */
  avvisoCondominio: (c: Condominio) => ReactNode;
  avvisoRiga: (k: Contatore) => ReactNode;
  className?: string;
}

const COLONNE = 7;
const COLONNE_COMPATTA = 6;
const TH = "px-1.5 py-2.5 align-bottom text-[10.5px] font-semibold uppercase leading-snug tracking-[0.03em] text-text xl:text-[11px] xl:tracking-[0.06em] first:pl-3 last:pr-3 xl:px-2 xl:first:pl-4 xl:last:pr-4";
// righe delle bollette (autocritica 24/10): 4 px sopra e sotto il valore alto 44 px (prima 10). Non 0 sotto quando segue
// la riga di note: una cella che va su tre righe supera i 44 px e la nota le finirebbe attaccata (misurato a 1024)
const TD = "px-1.5 py-1 first:pl-3 last:pr-3 xl:px-2 xl:first:pl-4 xl:last:pr-4";
// il ricarico finisce a destra e gli altri addebiti partono a sinistra: senza stacco sembrano attaccati (23/10 sera);
// 24 px invece di 32 perché la voce lunga («+2,14 € al mese: quota potenza…») stia su una riga a 1440 (autocritica 24/10)
const STACCO_ALTRI = "xl:pl-6";

/** Da 1024 px: una tabella sola per tutto il preventivo, come il report (spec § 7): dentro solo righe di dati, colonne di larghezza fissa (le intestazioni stanno alla stessa
 *  x per tutti i condomìni, come nel report approvato). Un <tbody> per condominio: riga di titolo, bollette,
 *  «Addebiti in più». Spec 2026-10-23 § 3.1, variante B del mockup. */
export function TabellaCondomini({ condomini, className = "", ...resto }: Props) {
  const larga = useSyncExternalStore(seguiLarga, eLarga);
  // il cestino in una colonna sua in fondo alla riga (03/11); nella copia per l'amministratore la colonna non c'è
  const { solaLettura } = useModo();
  const colElimina = !solaLettura && <col className="w-9" />;
  const thElimina = !solaLettura && <th scope="col" className={TH}><span className="sr-only">{ELIMINA}</span></th>;
  const compatta = useSyncExternalStore(seguiStretta, eStretta);
  const mese = colonnaMese(condomini);
  if (compatta) {
    return (
      <div className={`overflow-hidden rounded-lg border border-text/50 bg-card ${className}`}>
        <table className="w-full table-fixed text-[13px] leading-snug">
          {/* fra 640 e 1023 px (Alfonso 25/10): le colonne dei numeri larghe quanto il loro nome, «Mese · fornitura» prende il resto */}
          <colgroup>
            <col className="w-[44px]" />
            <col />
            <col className="w-[52px]" />
            <col className="w-[88px]" />
            <col className="w-[104px]" />
            <col className="w-[92px]" />
            {colElimina}
          </colgroup>
          <thead className="text-left">
            <tr className="border-b border-text/50">
              <th scope="col" className={TH}><DueRighe testo="" /></th>
              <th scope="col" className={TH}><DueRighe testo={COL_MESE_FORNITURA} /></th>
              <th scope="col" className={`${TH} text-right`}><DueRighe testo={COL_PCV} seconda="€/mese" /></th>
              <th scope="col" className={`${TH} text-right`}><DueRighe testo={COL_SPREAD} seconda="€/kWh" aCapo /></th>
              <th scope="col" className={`${TH} text-right`}><DueRighe testo={COL_RICARICO} seconda="in bolletta" aCapo /></th>
              <th scope="col" className={`${TH} text-right`}><DueRighe testo={COL_DIFFERENZA} seconda="al mese" /></th>
              {thElimina}
            </tr>
          </thead>
          {condomini.map((c) => <Gruppo key={c.chiave} c={c} larga={larga} compatta {...resto} />)}
        </table>
      </div>
    );
  }
  return (
    <div className={`overflow-hidden rounded-lg border border-text/50 bg-card ${className}`}>
      <table className="w-full table-fixed text-[13px] leading-snug xl:text-sm">
        <colgroup>
          {/* autocritica 24/10, misurate nel browser con le 16 bollette vere: le colonne dei numeri in pixel, larghe quanto
              il loro nome su una riga (variante 1) o il numero più lungo («6,18 € (0,017 €/kWh)»), più 1-2 px; il
              mese con la freccia e l'icona. Fornitura in proporzione (a 1440 «unico contatore, 1,5 kW» su una riga);
              «Altri addebiti» senza larghezza prende tutto il resto: a 1440 la voce lunga sta su una riga. Con le
              percentuali di prima a 1280 «Spread dichiarato» e «Ricarico non dichiarato» uscivano dalla cella, e a
              1440 «Differenza al mese» teneva 50 px vuoti. Fra 1280 e 1440 Fornitura resta più larga: lì il nome del
              condominio sta sulle prime quattro colonne (vedi LARGA) e con 14,2% andavano a capo due nomi in più. */}
          <col className={mese} />
          <col className="w-[11.1%] xl:w-[17%] min-[90rem]:w-[14.2%]" />
          <col className="w-[48px] xl:w-[56px]" />
          <col className="w-[130px] xl:w-[146px]" />
          <col className="w-[170px] xl:w-[188px]" />
          <col />
          <col className="w-[89px] xl:w-[102px]" />
          {colElimina}
        </colgroup>
        {/* come il report (spec § 7): maiuscolo piccolo, l'unità sulla seconda riga */}
        <thead className="text-left">
          <tr className="border-b border-text/50">
            <th scope="col" className={TH}><DueRighe testo="Mese" /></th>
            <th scope="col" className={TH}><DueRighe testo="Fornitura" /></th>
            <th scope="col" className={`${TH} text-right`}><DueRighe testo={COL_PCV} seconda="€/mese" /></th>
            <th scope="col" className={`${TH} text-right`}><DueRighe testo={COL_SPREAD} seconda="€/kWh" /></th>
            <th scope="col" className={`${TH} text-right`}><DueRighe testo={COL_RICARICO} seconda="in bolletta" /></th>
            <th scope="col" className={`${TH} ${STACCO_ALTRI}`}><DueRighe testo={COL_ALTRI} /></th>
            <th scope="col" className={`${TH} text-right`}><DueRighe testo={COL_DIFFERENZA} seconda="al mese" /></th>
            {thElimina}
          </tr>
        </thead>
        {condomini.map((c) => <Gruppo key={c.chiave} c={c} larga={larga} {...resto} />)}
      </table>
    </div>
  );
}

// Da 1440 px (autocritica 24/10) la riga del condominio si divide 5 + 2 invece di 4 + 3: «Altri addebiti» si è
// allargata a spese delle prime colonne e il nome dei condomìni lunghi andava a capo; il risparmio, anche con «su X
// delle Y», sta in «Altri addebiti» + «Differenza» (476 px a 1440). Sotto, a 1280, quelle due colonne sono 339 px:
// troppo poco per il risparmio più lungo, che resta su tre colonne. Il numero di colonne di una cella non si cambia
// col CSS: lo dice la finestra.
// 90rem = 1440 px, in rem come «xl» (80rem): la stessa soglia della colonna Fornitura (min-[90rem]), che in px finiva
// prima di «xl» nel CSS e non valeva
const LARGA = "(min-width: 90rem)";
function seguiLarga(avvisa: () => void) {
  const mq = window.matchMedia?.(LARGA);
  mq?.addEventListener("change", avvisa);
  return () => mq?.removeEventListener("change", avvisa);
}
const eLarga = () => window.matchMedia?.(LARGA).matches ?? false;

// Fra 640 e 1023 px (sotto i 640 la pagina mostra le schede e questa tabella non si vede): la tabella compatta, la stessa
// logica con una colonna in meno. Anche questa la dice la finestra, perché il numero di colonne non si cambia col CSS.
const STRETTA = "(max-width: 63.999rem)";
function seguiStretta(avvisa: () => void) {
  const mq = window.matchMedia?.(STRETTA);
  mq?.addEventListener("change", avvisa);
  return () => mq?.removeEventListener("change", avvisa);
}
const eStretta = () => window.matchMedia?.(STRETTA).matches ?? false;

type PropsGruppo = Omit<Props, "condomini" | "className"> & { c: Condominio; larga: boolean; compatta?: boolean };

function Gruppo({
  c, larga, compatta = false, offerte, aperto, occupato, onApriCondominio, onChiudi, onScegli, onScegliContatore, onElimina,
  avvisoCondominio, avvisoRiga, onPagina,
}: PropsGruppo) {
  const offerta = offertaDel(c, offerte);
  const { solaLettura } = useModo();
  const diverse = c.contatori.filter((k) => k.propria).length;
  const apertoQui = aperto?.condominio === c.chiave;
  const idPulsante = `${c.chiave}-offerta`;
  const parziale = suFornitture(c.contatori_contati, c.contatori_totali);
  // il risparmio prende le colonne del ricarico e di «Altri addebiti», vuote in questa riga: così sta su una riga anche
  // a 1024 px, anche con «su X delle Y» (misurato nel browser); da 1440 solo «Altri addebiti» e «Differenza»
  const colRisparmio = larga ? 2 : 3;
  const esiti = esitiConFrase(c.esiti);
  const colonne = (compatta ? COLONNE_COMPATTA : COLONNE) + (solaLettura ? 0 : 1);
  // il cestino della bolletta: `undefined` = niente colonna (copia per l'amministratore), `null` = cella vuota
  const cestino = (b: BollettaVista, qui: boolean) => (solaLettura ? undefined : qui ? <EliminaBolletta b={b} onElimina={onElimina} /> : null);
  const comuni = noteComuni(c.contatori.flatMap((k) => k.bollette), compatta);
  const viste = new Set<string>();
  // col gas (impostazione D): la riga verde ha il nome e il risparmio della luce; il venditore, le forniture e l'offerta
  // stanno nelle righe sottili «Luce» e «Gas». Senza gas il gruppo è quello di sempre.
  const gas = c.contatori_gas ?? [];
  const conGas = gas.length > 0;
  const soloGas = c.contatori.length === 0;
  const esitiGas = esitiGasVisibili(c.esiti_gas ?? []);
  const vistaGas = new Set<string>();
  // le bollette con gli addebiti aperti dalla freccia (più d'una alla volta)
  const [schede, setSchede] = useState<Set<string>>(() => new Set());
  const apriScheda = (id: string) => setSchede((x) => {
    const y = new Set(x);
    if (!y.delete(id)) y.add(id);
    return y;
  });
  // chiudendo con «Fatto» o con Esc il focus torna su chi ha aperto il riquadro
  const chiudiE = (id: string) => () => {
    const el = document.getElementById(id);
    onChiudi();
    el?.focus();
  };
  // il link dell'offerta del condominio (nella riga a una riga sola ha 12 px di stacco dal nome, nella compatta sta in una riga sua)
  const offertaEl = (stacco: string) => (
    // copia per l'amministratore: l'offerta scelta da leggere, nessun menù
    solaLettura ? (
      offerta && <span className={`${stacco} whitespace-nowrap align-middle text-xs font-medium text-text`}>{nomeConScadenza(offerta)}</span>
    ) : (
      <button
        type="button"
        id={idPulsante}
        aria-expanded={apertoQui}
        aria-controls={`${c.chiave}-riquadro`}
        onClick={() => onApriCondominio(c)}
        className={`-my-3 ${stacco} inline-flex min-h-11 items-center gap-1 whitespace-nowrap align-middle text-xs font-medium text-brand-dark underline underline-offset-2 cursor-pointer hover:text-brand ${FOCUS}`}
      >
        {offerta ? nomeConScadenza(offerta) : SCEGLI_OFFERTA}
        <ChevronDown size={12} aria-hidden="true" className={`shrink-0 transition-transform duration-200 motion-reduce:transition-none ${apertoQui ? "rotate-180" : ""}`} />
      </button>
    )
  );
  return (
    <tbody className="border-t-2 border-text/20 first-of-type:border-t-0">
      {/* riga del condominio sottile (spec § 7): nome · venditore · offerta attuale · forniture e il link dell'offerta;
          il risparmio del condominio nella colonna «Differenza al mese» */}
      {compatta ? (
        // fra 640 e 1023 px, su due righe: nome · fornitore · forniture e a destra la differenza; sotto l'offerta (link) e
        // «… l'anno» (mockup «tabella fold»)
        <tr className="bg-brand/10">
          <th scope="rowgroup" colSpan={colonne} className="px-3 py-2 text-left align-middle font-normal">
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0"><Intestazione c={c} inLinea soloNome={conGas} /></div>
              {offerta && c.mese !== null ? (
                <span className={`shrink-0 whitespace-nowrap text-sm font-semibold ${COLORE_TONO[tono(c.mese)]}`}><Importo x={c.mese} /> al mese</span>
              ) : soloGas ? null : <span className="shrink-0 text-muted-foreground">—</span>}
            </div>
            {!conGas && (
              <div className="text-xs text-muted-foreground">
                {offertaEl("")}
                {offerta && c.mese !== null && c.anno !== null && <> · {annoBreve(c.anno)}</>}
                {offerta && c.mese !== null && parziale && <> · {parziale}</>}
              </div>
            )}
            {conGas && offerta && c.mese !== null && (c.anno !== null || parziale) && (
              <div className="text-xs text-muted-foreground">
                {c.anno !== null && annoBreve(c.anno)}
                {c.anno !== null && parziale && " · "}
                {parziale}
              </div>
            )}
          </th>
        </tr>
      ) : (
      <tr className="bg-brand/10">
        <th scope="rowgroup" colSpan={7 - colRisparmio} className="py-2 pl-3 pr-2 text-left align-middle font-normal xl:pl-4">
          <Intestazione c={c} inLinea soloNome={conGas}>
            {!conGas && offertaEl("ml-3")}
          </Intestazione>
        </th>
        {/* il risparmio su una riga sola, che finisce sotto «Differenza al mese» e prende anche il posto vuoto di «Altri
            addebiti» (mockup «risparmio», variante 1): «−3,65 € al mese · −44 € l'anno», poi «su X delle Y» */}
        {/* finisce sotto «Differenza al mese»: col cestino dopo, quella non è più l'ultima colonna e ha il margine normale */}
        <td colSpan={colRisparmio} className={`py-2 pl-1.5 text-right align-middle xl:pl-2 ${solaLettura ? "pr-3 xl:pr-4" : "pr-1.5 xl:pr-2"}`}>
          {offerta && c.mese !== null ? (
            <p className="whitespace-nowrap leading-tight">
              <span className={`text-sm font-semibold ${COLORE_TONO[tono(c.mese)]}`}><Importo x={c.mese} /> al mese</span>
              {c.anno !== null && <span className="text-xs text-muted-foreground"> · {annoBreve(c.anno)}</span>}
              {parziale && <span className="text-xs text-muted-foreground"> · {parziale}</span>}
            </p>
          ) : soloGas ? null : <span className="text-muted-foreground">—</span>}
        </td>
        {!solaLettura && <td />}
      </tr>
      )}
      {/* la riga sottile della luce: il venditore, le forniture e l'offerta scelta (lo stesso link che senza gas sta nella riga verde) */}
      {conGas && !soloGas && (
        <tr>
          <td colSpan={colonne} className="px-3 pt-3 pb-1 xl:px-4">
            <EtichettaTipo tipo="Luce" venditore={c.venditore} n={c.n_forniture}>{offertaEl("ml-3")}</EtichettaTipo>
          </td>
        </tr>
      )}
      {apertoQui && (
        <tr className="border-t border-border bg-surface">
          <td colSpan={colonne} className="px-4 py-3">
            <div
              role="group"
              id={`${c.chiave}-riquadro`}
              aria-label={c.titolo}
              onKeyDown={escChiude(chiudiE(idPulsante))}
              className="flex flex-wrap items-end justify-end gap-3"
            >
              <div className="w-full max-w-[360px]">
                <p className="mb-2 text-xs font-medium text-muted-foreground">{offerta ? OFFERTA_CONDOMINIO : SCEGLI_OFFERTA}</p>
                <SceltaOfferta offerte={offerte} valore={offerta} onScegli={(id) => onScegli(c, id)} contesto="condominio" disabilitato={occupato} avviso={avvisoCondominio(c)} />
                {offerta && diverse > 0 && <p className="mt-2 text-xs text-muted-foreground">{fornitureDiverse(diverse)}</p>}
              </div>
              <PulsanteFatto onClick={chiudiE(idPulsante)} />
            </div>
          </td>
        </tr>
      )}
      {/* dopo il riquadro dell'offerta, che resta attaccato al pulsante che lo apre */}
      <RigaNote note={comuni} comuni colonne={colonne} />
      {c.contatori.flatMap((k) => {
        const offertaK = offertaDelContatore(k, c, offerte);
        // con una fornitura sola e nessuna offerta sua, i menù della riga sono quelli del condominio (anche senza offerta)
        const delCondominio = c.contatori.length === 1 && !k.propria;
        // i menù una volta per fornitura, sotto la sua ultima bolletta letta (03/11)
        const conMenu = delCondominio || offertaK !== null
          ? k.bollette.filter((b) => b.stato !== "non_letta").at(-1)?.id ?? null : null;
        return k.bollette.map((b) => {
          // una bolletta di più forniture compare su più righe: la freccia, la scheda ed «Elimina» solo sulla prima
          const prima = !viste.has(b.id);
          const voci = prima ? vociDi(esiti, b.id) : [];
          viste.add(b.id);
          const scheda = schede.has(b.id);
          const note = noteDi(b, compatta).filter((n) => !comuni.some(stessa(n)));
          return (
          <Fragment key={`${k.chiave}-${b.id}`}>
            <RigaBolletta
              b={b} k={k} offerta={offertaK}
              addebiti={voci.length > 0 ? { aperta: scheda, onApri: () => apriScheda(b.id) } : null}
              compatta={compatta}
              elimina={cestino(b, prima)}
            />
            <RigaNote note={note} colonne={colonne} prima={notaMotivo(b)}
              pagina={eIncompleta(b) && b.foto && onPagina && !solaLettura ? (
                <LinkPagina onClick={() => onPagina(b.id)} />
              ) : null}
              offerta={b.stato !== "non_letta" && k.propria && offertaK ? <>{nomeConScadenza(offertaK)}, {SOLO_QUESTA}</> : null}
              evidenziata={k.propria} />
            {/* la scheda c'è anche chiusa, nascosta: aria-controls della freccia punta sempre a qualcosa */}
            {voci.length > 0 && <SchedaAddebiti id={`${b.id}-addebiti`} voci={voci} aperta={scheda} colonne={colonne} />}
            {!solaLettura && (
              <RigaComandi
                colonne={colonne}
                evidenziata={k.propria}
                menu={conMenu === b.id ? (
                  <div role="group" aria-label={k.fornitura}>
                    <SceltaOfferta
                      inLinea
                      offerte={offerte}
                      valore={delCondominio ? offerta : offertaK}
                      onScegli={(id) => (delCondominio ? onScegli(c, id) : onScegliContatore(c, k, id))}
                      contesto={delCondominio ? "condominio" : "contatore"}
                      onTorna={k.propria ? () => onScegliContatore(c, k, null) : undefined}
                      disabilitato={occupato}
                      avviso={delCondominio ? avvisoCondominio(c) : avvisoRiga(k)}
                    />
                  </div>
                ) : null}
              />
            )}
          </Fragment>
          );
        });
      })}
      {conGas && (
        <>
          <tr>
            <td colSpan={colonne} className="px-3 pt-3 pb-1 xl:px-4">
              <EtichettaTipo tipo="Gas" venditore={c.venditore_gas ?? null} n={gas.length} />
            </td>
          </tr>
          {gas.flatMap((k) => k.bollette.map((b) => {
            const voci = vistaGas.has(b.id) ? [] : vociDi(esitiGas, b.id);
            // «Elimina» una volta per bolletta, anche per una bolletta luce e gas già in una riga luce
            const prima = !vistaGas.has(b.id) && !viste.has(b.id);
            vistaGas.add(b.id);
            const scheda = schede.has(b.id);
            return (
              <Fragment key={`${k.chiave}-${b.id}`}>
                <RigaGas b={b} k={k} compatta={compatta} addebiti={voci.length > 0 ? { aperta: scheda, onApri: () => apriScheda(b.id) } : null}
                  elimina={cestino(b, prima)} />
                <RigaAvvisoGas b={b} colonne={colonne} compatta={compatta} />
                {voci.length > 0 && <SchedaAddebiti id={`${b.id}-addebiti`} voci={voci} aperta={scheda} colonne={colonne} />}
              </Fragment>
            );
          }))}
        </>
      )}
    </tbody>
  );
}

/** La freccia che apre gli addebiti di una bolletta (luce o gas); `null` = nessun addebito: lo spazio resta. */
function FrecciaAddebiti({ b, addebiti }: { b: BollettaVista; addebiti: { aperta: boolean; onApri: () => void } | null }) {
  return addebiti ? (
    <button
      type="button"
      aria-label={ADDEBITI_IN_PIU}
      title={ADDEBITI_IN_PIU}
      aria-expanded={addebiti.aperta}
      aria-controls={`${b.id}-addebiti`}
      onClick={addebiti.onApri}
      className={`relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border bg-card text-text cursor-pointer hover:bg-surface after:absolute after:-inset-[11px] after:content-[''] ${FOCUS}`}
    >
      <ChevronDown size={14} aria-hidden="true" className={`transition-transform duration-200 motion-reduce:transition-none ${addebiti.aperta ? "rotate-180" : ""}`} />
    </button>
  ) : <span aria-hidden="true" className="w-6 shrink-0" />;
}

/** Una bolletta gas (giro 1 del gas): Mese, Fornitura, PCV e Altri addebiti; spread, ricarico e differenza vuoti (nessun
 *  «—»: il gas non si confronta con le offerte), nessun pulsante dell'offerta. Con la tabella compatta «Altri addebiti»
 *  sta nella riga delle note, come per la luce. */
function RigaGas({ b, k, addebiti, compatta = false, elimina }: {
  b: BollettaVista; k: ContatoreGas; addebiti: { aperta: boolean; onApri: () => void } | null; compatta?: boolean; elimina?: ReactNode;
}) {
  const Val = compatta ? ValoreCompatto : Valore;
  const fornitura = (k.fornitura ?? "").replace(/(\d) kW\b/g, "$1\u00a0kW");
  const freccia = <FrecciaAddebiti b={b} addebiti={addebiti} />;
  const vuote = compatta ? 3 : 4;      // spread, ricarico, (altri) e differenza
  return (
    <tr className="border-t border-border/70 align-top">
      {compatta ? (
        <>
          <td className={`${TD} pt-2`}>{freccia}</td>
          <td className={`${TD} pt-2`}>
            <div><MeseLink b={b} compatta /></div>
            <div className="break-words text-text">{fornitura}</div>
          </td>
        </>
      ) : (
        <>
          <td className={`${TD} whitespace-nowrap`}>
            <div className="flex min-h-11 items-center gap-2.5">
              {freccia}
              <MeseLink b={b} aCapo />
            </div>
          </td>
          <td className={`${TD} break-words text-text`}><Valore>{fornitura}</Valore></td>
        </>
      )}
      <td className={`${TD} ${compatta ? "pt-2 " : ""}whitespace-nowrap text-right tabular-nums`}><Val><Pcv b={b} /></Val></td>
      {b.dettaglio === "in_corso" && (
        <td colSpan={vuote} aria-busy="true" className={`${TD} text-center text-sm italic text-muted-foreground`}>
          <Valore><span className="animate-pulse motion-reduce:animate-none">{IN_ANALISI}</span></Valore>
        </td>
      )}
      {b.dettaglio === "non_leggibile" && (
        <td colSpan={vuote} className={`${TD} text-muted-foreground`}><Valore>{DETTAGLIO_NON_LEGGIBILE}</Valore></td>
      )}
      {b.dettaglio === "pronto" && (
        <>
          <td className={TD} />
          <td className={TD} />
          {/* analisi non fatta: la cella resta vuota, un «—» direbbe «niente da segnalare» (ruling 53) */}
          {!compatta && <td className={`${TD} ${STACCO_ALTRI} break-words`}>{avvisoGas(b) === null && <Valore><Altri b={b} senzaNote /></Valore>}</td>}
          <td className={TD} />
        </>
      )}
      <CellaCestino elimina={elimina} />
    </tr>
  );
}

/** Sotto la riga gas: «Altri addebiti» (tabella compatta) e, se l'AI non ha controllato le voci, perché. */
function RigaAvvisoGas({ b, colonne, compatta }: { b: BollettaVista; colonne: number; compatta: boolean }) {
  const avviso = avvisoGas(b);
  const altri = compatta && b.dettaglio === "pronto" && b.altri.length > 0 ? altriInFila(b.altri) : null;
  if (avviso === null && altri === null) return null;
  return (
    <tr>
      <td />
      <td colSpan={colonne - 1} className="pb-2 pl-1.5 pr-3 pt-0 text-xs leading-snug text-muted-foreground xl:pl-2 xl:pr-4">
        {altri !== null && <><span className="font-medium text-text/70">{NOTA_ALTRI}</span> {altri}</>}
        {altri !== null && avviso !== null && " · "}
        {avviso}
      </td>
    </tr>
  );
}

interface PropsRiga {
  b: BollettaVista;
  k: Contatore;
  offerta: OffertaMenu | null;
  /** La freccia degli addebiti di questa bolletta; `null` = nessun addebito (lo spazio resta, i mesi in colonna). */
  addebiti: { aperta: boolean; onApri: () => void } | null;
  /** Tabella compatta, fra 640 e 1023 px. */
  compatta?: boolean;
  /** Il cestino in fondo alla riga: `undefined` = niente colonna, `null` = cella vuota (la bolletta ha già il suo). */
  elimina?: ReactNode;
}

/** Una bolletta: i numeri di oggi e, nell'ultima colonna, la differenza al mese con l'offerta della fornitura (l'offerta
 *  si cambia dai menù nella riga sottile sotto, 03/11). Nella tabella compatta (`compatta`, 640-1023 px) la freccia ha
 *  una colonna sua, il mese sta sopra la fornitura nella stessa cella, il ricarico su due righe e «Altri addebiti» non
 *  c'è (sta nella riga delle note). */
function RigaBolletta({ b, k, offerta, addebiti, compatta = false, elimina }: PropsRiga) {
  // una bolletta incompleta ha i numeri letti come le altre, senza la differenza
  const incompleta = eIncompleta(b);
  const letta = b.stato !== "non_letta" || incompleta;
  const diff = differenzaBolletta(b, offerta);
  const fornitura = k.fornitura.replace(/(\d) kW\b/g, "$1\u00a0kW");
  const Val = compatta ? ValoreCompatto : Valore;
  const freccia = <FrecciaAddebiti b={b} addebiti={addebiti} />;
  return (
    <tr className={`border-t border-border/70 align-top ${k.propria ? "bg-brand/5" : ""}`}>
      {compatta ? (
        <>
          <td className={`${TD} pt-2`}>{freccia}</td>
          <td className={`${TD} pt-2`}>
            <div><MeseLink b={b} compatta /></div>
            <div className="break-words text-text">{fornitura}</div>
          </td>
        </>
      ) : (
        <>
          <td className={`${TD} whitespace-nowrap`}>
            {/* gap-2.5 = la sporgenza dell'area di tocco della freccia fuori dal bordo (after:-inset-[11px] dal padding, 10 px
                dal bordo: 24 + 20 = 44 px; con -inset-2.5 erano 42): il mese apre sempre il file */}
            <div className="flex min-h-11 items-center gap-2.5">
              {freccia}
              <MeseLink b={b} aCapo />
            </div>
          </td>
          <td className={`${TD} break-words text-text`}><Valore>{fornitura}</Valore></td>
        </>
      )}
      {!letta && <td colSpan={compatta ? 4 : 5} className={TD}><NonLetta className="min-h-11" /></td>}
      {letta && <td className={`${TD} ${compatta ? "pt-2 " : ""}whitespace-nowrap text-right tabular-nums`}><Val><Pcv b={b} /></Val></td>}
      {/* variante A: i numeri su una riga, le note nella riga sotto; «Altri addebiti» va a capo con più voci */}
      {letta && b.dettaglio === "pronto" && (
        <>
          <td className={`${TD} ${compatta ? "pt-2 " : ""}whitespace-nowrap text-right tabular-nums`}><Val><Spread b={b} senzaNote /></Val></td>
          <td className={`${TD} ${compatta ? "pt-2 tabular-nums " : ""}whitespace-nowrap text-right`}><Val><Ricarico b={b} senzaNote dueRighe={compatta} /></Val></td>
          {/* una voce sola su una riga da 1280 px; a 1024 non c'è posto per tutto: va a capo invece di uscire dalla cella */}
          {!compatta && <td className={`${TD} ${STACCO_ALTRI} break-words`}><Valore><Altri b={b} senzaNote /></Valore></td>}
        </>
      )}
      {letta && b.dettaglio === "in_corso" && (
        <>
          <td colSpan={compatta ? 2 : 3} aria-busy="true" className={`${TD} text-center text-sm italic text-muted-foreground`}>
            <Valore><span className="animate-pulse motion-reduce:animate-none">{IN_ANALISI}</span></Valore>
          </td>
        </>
      )}
      {letta && b.dettaglio === "non_leggibile" && (
        <td colSpan={compatta ? 2 : 3} className={`${TD} text-muted-foreground`}><Valore>{DETTAGLIO_NON_LEGGIBILE}</Valore></td>
      )}
      {incompleta && (
        <td className={`${TD} ${compatta ? "pt-2 " : ""}text-right`}>
          <span className={`flex ${compatta ? "min-h-6" : "min-h-11"} items-center justify-end text-muted-foreground`}>—</span>
        </td>
      )}
      {letta && !incompleta && (
        // solo la cifra (03/11: l'offerta si cambia dai menù nella riga sottile, non più dalla matita); letta ma senza
        // cifra per quest'offerta: cella vuota, il contatore resta fuori dalla somma
        <td className={`${TD} ${compatta ? "pt-2 " : ""}text-right`}>
          <div className={`flex ${compatta ? "min-h-6" : "min-h-11"} flex-col items-end justify-center`}>
            {diff !== null && <span className="font-semibold"><Importo x={diff} className="whitespace-nowrap" /></span>}
          </div>
        </td>
      )}
      <CellaCestino elimina={elimina} />
    </tr>
  );
}

type Nota = [etichetta: string, testo: string];

/** Le note di una bolletta, nell'ordine «Spread · Ricarico · Altri addebiti», solo quelle che ci sono. Le parole sono
 *  quelle che prima stavano sotto i numeri. */
function noteDi(b: BollettaVista, compatta = false): Nota[] {
  if ((b.stato === "non_letta" && !eIncompleta(b)) || b.dettaglio !== "pronto") return [];
  const note: Nota[] = [];
  if (spreadScritto(b.spread) === null) {
    const mancante = notaSpreadMancante(b);
    if (mancante !== null) note.push([NOTA_SPREAD, mancante]);
  }
  else if (b.spread_nota) note.push([NOTA_SPREAD, b.spread_nota.includes("«") ? b.spread_nota : `«${b.spread_nota}»`]);
  if (b.ricarico !== null && b.ricarico_dove) note.push([NOTA_RICARICO, b.ricarico_dove]);
  if (compatta) {
    // tabella compatta: «Altri addebiti» non è una colonna, le voci vanno qui; con le spese per insoluto la nota è una sola
    if (b.altri.length > 0) {
      const fuori = b.altri.some((a) => a.fuori_confronto);
      note.push([NOTA_ALTRI, `${altriInFila(b.altri)}${fuori ? ` · ${FUORI_CONFRONTO}` : ""}`]);
    }
  } else if (b.altri.some((a) => a.fuori_confronto)) note.push([NOTA_ALTRI, FUORI_CONFRONTO]);
  return note;
}

const stessa = (x: Nota) => (y: Nota) => x[0] === y[0] && x[1] === y[1];

/** Le note uguali (stessa etichetta, stesso testo) in tutte le bollette del condominio (autocritica 24/10): si scrivono
 *  una volta sola sotto la riga del condominio. Con una bolletta sola nessuna; una bolletta senza note (non letta, in
 *  analisi) non le ha, quindi nessuna nota è di tutte. */
function noteComuni(bollette: BollettaVista[], compatta = false): Nota[] {
  if (new Set(bollette.map((b) => b.id)).size < 2) return [];
  const [prima, ...altre] = bollette.map((b) => noteDi(b, compatta));
  // gli addebiti sono di ciascuna bolletta: anche se uguali non diventano una nota del condominio
  return prima.filter((n) => !(compatta && n[0] === NOTA_ALTRI) && altre.every((x) => x.some(stessa(n))));
}

/** Le note fuori dalle celle (mockup «impaginazioni», variante A): una riga sotto i numeri della bolletta, dalla
 *  colonna Fornitura in poi, «Spread: … · Ricarico: … · Altri addebiti: …». Nessuna riga se non ce n'è nessuna. Le
 *  note comuni a tutto il condominio stanno nella stessa riga sotto quella del condominio, prima delle bollette. */
function RigaNote({ note, colonne = COLONNE, comuni = false, offerta = null, evidenziata = false, prima = null, pagina = null }: {
  note: Nota[]; colonne?: number; comuni?: boolean; offerta?: ReactNode;
  /** Il dato che manca (o «rete e oneri come in bolletta»): la prima nota della riga, poi «Aggiungi pagina». */
  prima?: Nota | null; pagina?: ReactNode;
  /** La riga sopra è di una fornitura con offerta sua: stesso fondo, l'evidenziazione non si spezza (giro 5). */
  evidenziata?: boolean;
}) {
  if (note.length === 0 && offerta === null && prima === null && pagina === null) return null;
  const tutte = prima ? [prima, ...note] : note;
  return (
    <tr className={evidenziata ? "bg-brand/5" : undefined}>
      <td />
      <td colSpan={colonne - 1} className={`${comuni ? "pt-2" : "pt-0"} pb-2 pl-1.5 pr-3 text-xs leading-snug text-muted-foreground xl:pl-2 xl:pr-4`}>
        {tutte.map(([etichetta, testo], i) => (
          <Fragment key={etichetta}>
            {i > 0 && " · "}
            <span className="font-medium text-text/70">{etichetta}</span> {testo}
            {i === 0 && pagina !== null && <> · {pagina}</>}
          </Fragment>
        ))}
        {tutte.length === 0 && pagina}
        {offerta !== null && (
          <>
            {(tutte.length > 0 || pagina !== null) && " · "}
            <span className="font-medium text-text/70">{NOTA_OFFERTA}</span> {offerta}
          </>
        )}
      </td>
    </tr>
  );
}

/** «Aggiungi pagina» nella riga delle note di una bolletta fotografata a cui manca un dato: come un'offerta nelle note,
 *  con l'area di tocco di 44 px che sporge sopra e sotto senza alzare la riga. */
function LinkPagina({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative font-medium text-brand-dark underline underline-offset-2 cursor-pointer hover:text-brand after:absolute after:-inset-x-1 after:-inset-y-[14px] after:content-[''] rounded-sm ${FOCUS}`}
    >
      {AGGIUNGI_PAGINA}
    </button>
  );
}

/** La colonna Mese, larga quanto serve al mese più lungo della tabella (giro 4 del link: il mese doppio su una riga
 *  sola dai 1280 px), misurata nel browser con freccia, mese e icona: un mese solo 138 px come prima, «Ago-Set 2026»
 *  161, «Dic 2025-Gen 2026» 197. Sotto i 1280 px resta 131 (il mese doppio va a capo, vedi `MeseLink`). Lo spazio si
 *  toglie alle altre colonne solo quando c'è un mese che lo vuole. */
function colonnaMese(condomini: Condominio[]): string {
  const piuLungo = Math.max(0, ...condomini.flatMap((c) => [...c.contatori, ...(c.contatori_gas ?? [])].flatMap((k) => k.bollette.map((b) => b.mese_label?.length ?? 0))));
  if (piuLungo > "Ago-Set 2026".length) return "w-[131px] xl:w-[197px]";
  if (piuLungo > "Set 2026".length) return "w-[131px] xl:w-[161px]";
  return "w-[131px] xl:w-[138px]";
}

/** Come `Valore`, per la tabella compatta: il numero sta sulla riga del mese (24 px), un secondo rigo scende sotto. */
function ValoreCompatto({ children }: { children: ReactNode }) {
  return <div className="flex min-h-6 flex-col justify-start leading-6">{children}</div>;
}

/** Il valore di una cella allineato al testo del link del mese (alto 44 px): la riga si legge dritta. */
function Valore({ children }: { children: ReactNode }) {
  return <div className="flex min-h-11 flex-col justify-center">{children}</div>;
}

/** Un'intestazione sempre su due righe allineate in basso (mockup «risparmio», variante 1): il nome sopra, che non va a
 *  capo; sotto l'unità in minuscolo grigio («€/mese», «in bolletta»), vuota dove non c'è. Il testo resta quello
 *  approvato: «PCV €/mese» = «PCV» + «€/mese». Un'etichetta che non finisce più con la seconda riga si scrive intera
 *  sopra, senza storpiarla (il test delle intestazioni se ne accorge). `aCapo` (tabella compatta, colonne strette): il nome
 *  può andare a capo fra le parole, l'unità resta sotto. */
function DueRighe({ testo, seconda = "", aCapo = false }: { testo: string; seconda?: string; aCapo?: boolean }) {
  const divisa = seconda !== "" && testo.endsWith(` ${seconda}`);
  const nome = divisa ? testo.slice(0, testo.length - seconda.length).trim() : testo;
  return (
    <>
      <span className={`block ${aCapo ? "" : "whitespace-nowrap"}`}>{nome}</span>
      {divisa ? " " : ""}
      <span className="block h-4 font-normal normal-case tracking-normal text-muted-foreground">{divisa ? seconda : ""}</span>
    </>
  );
}

/** Le voci di una bolletta: per ogni esito con frasi di quella bolletta, l'esito con solo quelle frasi. */
function vociDi(esiti: Esito[], bollettaId: string): Esito[] {
  return esiti
    .map((e) => ({ ...e, voci: e.voci.filter((v) => v.bolletta_id === bollettaId) }))
    .filter((e) => e.voci.length > 0);
}

/** Sotto la riga della bolletta, gli addebiti di quella bolletta: righe allineate, pillola e titolo a sinistra in una
 *  colonna fissa, la frase a destra (spec § 7, variante B). La fornitura non si ripete: la dice la riga sopra. */
function SchedaAddebiti({ id, voci, aperta, colonne }: { id: string; voci: Esito[]; aperta: boolean; colonne: number }) {
  return (
    <tr hidden={!aperta}>
      <td colSpan={colonne} className="px-3 pb-3 pt-0 xl:px-4">
        <dl id={id} className="overflow-hidden rounded-md border border-text/20 text-sm">
          {voci.map((e) => (
            <div key={`${e.giudizio}-${e.titolo}`} className="grid grid-cols-[15rem_minmax(0,1fr)] border-t border-border bg-surface/60 first:border-t-0 xl:grid-cols-[19rem_minmax(0,1fr)]">
              <dt className="min-w-0 px-3 py-2">
                <Pillola giudizio={e.giudizio} />
                <span className="mt-1 block font-semibold text-text break-words">{e.titolo}</span>
              </dt>
              <dd className="min-w-0 px-3 py-2 text-muted-foreground">
                {e.voci.filter((v) => v.frase).map((v, i) => <p key={i} className="max-w-[75ch] break-words">{conUnita(v.frase)}</p>)}
              </dd>
            </div>
          ))}
        </dl>
      </td>
    </tr>
  );
}

function PulsanteFatto({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-12 rounded-lg bg-brand-dark px-4 text-sm font-semibold text-white cursor-pointer hover:bg-dark transition-colors ${FOCUS} focus-visible:ring-offset-2`}
    >
      {FATTO}
    </button>
  );
}

/** Esc dentro un riquadro lo chiude (revisione 1, M8). */
function escChiude(chiudi: () => void) {
  return (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    chiudi();
  };
}

/** La riga sottile sotto la bolletta (Alfonso 03/11, «riga sotto»): «Elimina» a sinistra, i menù dell'offerta della
 *  fornitura a destra (sotto la sua ultima bolletta letta), sempre aperti. Nessuna riga se non c'è né l'uno né l'altro. */
function RigaComandi({ colonne, evidenziata, menu }: { colonne: number; evidenziata: boolean; menu: ReactNode }) {
  if (menu === null) return null;
  return (
    <tr className={evidenziata ? "bg-brand/5" : undefined}>
      <td colSpan={colonne} className="px-3 pb-2 pt-0 xl:px-4">
        <div className="flex justify-end">{menu}</div>
      </td>
    </tr>
  );
}

/** L'ultima cella della riga di una bolletta: il cestino (32 px) al centro della sua colonna da 36, in linea coi numeri
 *  (alto 44 come un valore, 4 px sopra e sotto come le altre celle). */
function CellaCestino({ elimina }: { elimina: ReactNode | undefined }) {
  if (elimina === undefined) return null;
  return <td className="px-0.5 py-1"><div className="flex min-h-11 items-center justify-center">{elimina}</div></td>;
}
